import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Bridge } from '@casa/bridge';
import { approveCloudDevice, cloudLink, readCloudKeys, type CloudDevice } from '../data/cloud_secrets.ts';
import { fingerprintOf } from '../secrets/keys.ts';
import { AllowDeviceDialog } from './allow_device_dialog.tsx';

// The prompt on a home's devices when another wants to join (docs/specs/cloud-joining.md JN-4):
// while the app is open, a device of the home that has the key asks Tilez Cloud every 30 seconds
// whether one is waiting for it, and offers to let it in. "Not now" hides that device here until
// the app is opened again. Nothing is asked of a device not on Tilez Cloud, or without the key.

const ASK_EVERY_MS = 30_000;

export function JoinRequests({ shell }: { shell: Bridge }) : ReactNode {
    const [waiting, setWaiting] = useState<{ device: CloudDevice; code: string }>();
    const [revision, setRevision] = useState(0);
    /** The devices this app was told about and put off, until it is opened again. */
    const putOff = useRef(new Set<string>());

    useEffect(() => {
        if (waiting) {
            return;
        }

        let isCurrent = true;

        const look = async () : Promise<void> => {
            try {
                const link = await cloudLink(shell);
                const keys = link ? await readCloudKeys(shell, link) : undefined;

                if (!keys?.mine) {
                    return;
                }

                const device = keys.devices.find((each) => each.id !== keys.me && !each.hasKey && !putOff.current.has(each.id));

                if (device && isCurrent) {
                    setWaiting({ device, code: await fingerprintOf(device.publicKey) });
                }
            } catch {
                // Offline, or not on Tilez Cloud any more: asked again in a while.
            }
        };

        void look();

        const timer = setInterval(() => void look(), ASK_EVERY_MS);

        return () => {
            isCurrent = false;
            clearInterval(timer);
        };
    }, [shell, waiting, revision]);

    if (!waiting) {
        return null;
    }

    return (
        <AllowDeviceDialog
            device={waiting.device}
            code={waiting.code}
            onClose={() => {
                putOff.current.add(waiting.device.id);
                setWaiting(undefined);
            }}
            onAllow={async () => {
                const link = await cloudLink(shell);

                if (link) {
                    await approveCloudDevice(shell, link, waiting.device);
                }

                setWaiting(undefined);
                setRevision((value) => value + 1);
            }}
        />
    );
}
